import React from "react";
import { ArrowRight } from "lucide-react";

/**
 * Big-statement section: "We invented the selfie cam in 2023."
 * Floating gradient blobs (cyan + violet + magenta) hint at the playful
 * 3D shapes from the SimpleBooth reference, but recolored to fit Stori's
 * dark / cyan aesthetic.
 */
export default function InventedSelfieCam() {
  return (
    <section
      data-testid="invented-selfie-cam"
      className="relative overflow-hidden border-b border-stori-border py-32 lg:py-44 bg-stori-bg"
    >
      {/* Ambient backdrop */}
      <div className="absolute inset-0 grid-noise opacity-30 pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-stori-cyan/12 rounded-full blur-[140px] pointer-events-none" />

      {/* Floating gradient shapes */}
      <Blob className="top-10 left-[6%] w-24 h-24" rotate="rotate-[-22deg]" />
      <Ring className="top-16 right-[8%] w-36 h-36" />
      <Squiggle className="top-1/3 -left-8 w-40" rotate="rotate-[-18deg]" />
      <Squiggle
        className="bottom-12 right-[6%] w-44"
        rotate="rotate-[14deg]"
        gradient="warm"
      />
      <Ring
        className="bottom-16 left-[10%] w-28 h-28"
        gradient="from-fuchsia-400 via-pink-400 to-orange-300"
      />
      <Blob
        className="top-1/2 right-[14%] w-20 h-20"
        rotate="rotate-[40deg]"
        gradient="from-stori-cyan to-fuchsia-400"
      />

      {/* Copy */}
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          ORIGIN
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>

        <h2
          data-testid="invented-selfie-cam-headline"
          className="font-heading font-semibold text-white tracking-tight leading-[0.98] text-[clamp(3rem,9vw,8rem)]"
        >
          <span className="block fade-up">We invented</span>
          <span
            className="block fade-up text-stori-cyan cyan-text-glow"
            style={{ animationDelay: "120ms" }}
          >
            the selfie cam
          </span>
          <span className="block fade-up" style={{ animationDelay: "240ms" }}>
            in 2013.
          </span>
        </h2>

        <p className="mt-12 lg:mt-14 text-base lg:text-lg text-stori-muted max-w-xl mx-auto leading-relaxed">
          Smarter. More shareable. A whole lot more profitable.
        </p>

        <div className="mt-10 flex justify-center">
          <a
            href="#experiences"
            data-testid="invented-selfie-cam-cta"
            className="group inline-flex items-center gap-2 bg-stori-cyan text-black font-semibold px-7 py-4 rounded-md hover:bg-stori-cyanHover transition-all hover:translate-y-[-1px]"
          >
            See the platform
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Decorative shape primitives
// ---------------------------------------------------------------------------

function Blob({ className = "", rotate = "", gradient = "from-stori-cyan via-cyan-300 to-fuchsia-400" }) {
  return (
    <div
      aria-hidden="true"
      className={`absolute pointer-events-none rounded-[40%_60%_55%_45%/45%_55%_45%_55%] bg-gradient-to-br ${gradient} blur-[2px] shadow-[0_10px_40px_rgba(0,229,255,0.25)] opacity-90 ${rotate} ${className}`}
    />
  );
}

function Ring({ className = "", gradient = "from-stori-cyan via-cyan-300 to-fuchsia-400" }) {
  return (
    <div
      aria-hidden="true"
      className={`absolute pointer-events-none rounded-full ${className}`}
      style={{
        background: "transparent",
      }}
    >
      <div
        className={`w-full h-full rounded-full bg-gradient-to-br ${gradient} blur-[1px] opacity-90 shadow-[0_10px_40px_rgba(0,229,255,0.25)]`}
        style={{
          WebkitMask:
            "radial-gradient(circle at center, transparent 52%, black 53%)",
          mask: "radial-gradient(circle at center, transparent 52%, black 53%)",
        }}
      />
    </div>
  );
}

function Squiggle({ className = "", rotate = "", gradient = "cyan" }) {
  const gradId = `sq-${gradient}`;
  const stops =
    gradient === "warm"
      ? [
          { offset: "0%", color: "#F472B6" },
          { offset: "50%", color: "#FBBF24" },
          { offset: "100%", color: "#FB923C" },
        ]
      : [
          { offset: "0%", color: "#00E5FF" },
          { offset: "100%", color: "#E879F9" },
        ];
  return (
    <div
      aria-hidden="true"
      className={`absolute pointer-events-none ${rotate} ${className}`}
    >
      <svg viewBox="0 0 220 80" className="w-full h-auto" fill="none">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="220" y2="80" gradientUnits="userSpaceOnUse">
            {stops.map((s) => (
              <stop key={s.offset} offset={s.offset} stopColor={s.color} />
            ))}
          </linearGradient>
        </defs>
        <path
          d="M5 60 C 40 0, 80 100, 110 40 S 180 0, 215 60"
          stroke={`url(#${gradId})`}
          strokeWidth="22"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </div>
  );
}
